import {
  CountryCodeSelect,
  FormControlRadioGroup,
  HelperText,
  InputAddInfoField,
  PhoneNumberInput,
  PhoneNumberLayout,
} from "../AddPatientGroup.style";
import * as React from "react";
import { useEffect, useState } from "react";
import { Box } from "@mui/material";
import {
  CountryCode,
  InfoDetails,
  SortOrder,
} from "@home-health-notify/domain";
import {
  formatPhoneNumberInput,
  isValidPhoneNo,
  useTranslation,
} from "hhn-commons";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";

type InfoFieldFormProps = {
  name: string;
  onSetAddInfoFields: (name: string, value: number) => void;
  onSetFormData: (name: string, data: InfoDetails) => void;
  infoKey: string;
  infoDetail: InfoDetails;
};

export const InfoFieldForm = ({
  name,
  onSetAddInfoFields,
  onSetFormData,
  infoKey,
  infoDetail,
}: InfoFieldFormProps) => {
  const [infoData, setInfoData] = useState<InfoDetails>(infoDetail);
  const [addInfoPhoneNumberErrorMessage, setAddInfoPhoneNumberErrorMessage] =
    useState<string>("");
  const isPrimary: boolean = infoData.sortOrder === SortOrder.PRIMARY;

  const { t } = useTranslation();
  const tNs = (key: string): string => t(key, { ns: "patientGroup" });

  useEffect(() => {
    infoDetail && setInfoData(infoDetail);
  }, [infoDetail]);

  const onInfoDataChange = (key: string, value: any) => {
    const data: InfoDetails = { ...infoData, [key]: value };
    setInfoData(() => {
      return data;
    });
    onSetFormData(name, data);
  };

  const handlePhoneChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target.value;
    let newStr = input && input.replace(/-|(\()|(\))| /g, "");
    onInfoDataChange("phoneNo", newStr);
  };

  function checkPhoneNumberLength(number: string) {
    if (number.length > 0) {
      // Reuse validateAddInfo's check so this can't drift from what blocks submission.
      setAddInfoPhoneNumberErrorMessage(
        isValidPhoneNo(number) ? "" : tNs("invalidPhoneNumber"),
      );
    }
  }

  // Pre-populated phoneNo (edit, new episode) never hits onBlur/onChange,
  // so validate it on load too.
  useEffect(() => {
    if (infoData.phoneNo) {
      checkPhoneNumberLength(infoData.phoneNo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [infoData.phoneNo]);

  const onChangeType = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = +event.target.value;
    onSetAddInfoFields(name, value);
  };

  return (
    <Box>
      <InputAddInfoField
        id="full-name"
        label={tNs("fullNameLabel")}
        type="text"
        variant="standard"
        value={infoData.name}
        slotProps={{
          htmlInput: {
            "data-testid": infoKey + "-full-name",
            maxLength: 50,
          },
        }}
        onChange={e => {
          onInfoDataChange("name", e.target.value);
        }}
        error={!!(infoData.phoneNo && !infoData.name.trim())}
      />
      <PhoneNumberLayout>
        <CountryCodeSelect
          value={infoData.countryCode}
          data-testid="country-code"
          onChange={event =>
            onInfoDataChange("countryCode", event.target.value as CountryCode)
          }
          id="selectCountryCode"
        >
          <option value={CountryCode.US}>
            ({CountryCode.US}) United States
          </option>
          <option value={CountryCode.INDIA}>({CountryCode.INDIA}) India</option>
          <option value={CountryCode.PHILIPPINES}>
            ({CountryCode.PHILIPPINES}) Philippines
          </option>
        </CountryCodeSelect>
        <PhoneNumberInput
          variant="standard"
          id="phone-number"
          label={tNs("phoneNumber")}
          value={formatPhoneNumberInput(infoData.phoneNo, infoData.countryCode)}
          slotProps={{
            htmlInput: {
              "data-testid": infoKey + "-phone-number",
              maxLength: infoData.countryCode === "+1" ? 14 : 10,
            },
          }}
          onBlur={e => checkPhoneNumberLength(e.target.value)}
          onChange={handlePhoneChange}
          error={
            !!(
              addInfoPhoneNumberErrorMessage !== "" ||
              (infoData.name.trim() && !infoData.phoneNo)
            )
          }
        />
      </PhoneNumberLayout>
      <HelperText>
        {addInfoPhoneNumberErrorMessage !== ""
          ? addInfoPhoneNumberErrorMessage
          : " "}
      </HelperText>
      <FormControlRadioGroup>
        <RadioGroup
          row
          aria-labelledby="demo-radio-buttons-group-label"
          name="radio-buttons-group"
          value={isPrimary ? 1 : 2}
          onChange={onChangeType}
        >
          <FormControlLabel value={1} control={<Radio />} label="Primary" />
          <FormControlLabel value={2} control={<Radio />} label="Secondary" />
        </RadioGroup>
      </FormControlRadioGroup>
    </Box>
  );
};
